import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { carUrl } from '../lib/slugId';
import type { Car, Paginated, SingleResponse, User } from '../api/types';
import { CarCardMedia } from '../components/CarCardMedia';

/**
 * Public profile of another member: who they are plus the cars in their garage.
 * Reached from anywhere a member is credited (e.g. an event organizer).
 */
export function UserProfile() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    Promise.all([
      apiFetch<SingleResponse<User>>(`/api/v1/users/${id}`),
      apiFetch<Paginated<Car>>(`/api/v1/cars?filter[user_id]=${id}&filter[is_public]=1&per_page=50`),
    ])
      .then(([userRes, carsRes]) => {
        setUser(userRes.data);
        setCars(carsRes.data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <Alert severity="error">{error || t('userProfile.notFound')}</Alert>
      </Container>
    );
  }

  const location = [user.city, user.country].filter(Boolean).join(', ');

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader crumbs={[{ label: user.name }]} />

      <Stack spacing={4}>
        <Card>
          <CardContent>
            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar src={user.avatar_url ?? undefined} sx={{ width: 96, height: 96 }}>
                {user.name[0]}
              </Avatar>
              <Stack spacing={0.5}>
                <Typography variant="h5">{user.name}</Typography>
                {user.username && (
                  <Typography variant="body2" color="text.secondary">
                    @{user.username}
                  </Typography>
                )}
                {location && (
                  <Typography variant="body2" color="text.secondary">
                    {location}
                  </Typography>
                )}
              </Stack>
            </Stack>
            {user.bio && (
              <Typography variant="body1" sx={{ mt: 2 }}>
                {user.bio}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Stack spacing={2}>
          <Typography variant="h5">{t('userProfile.cars')}</Typography>
          {cars.length === 0 ? (
            <Typography color="text.secondary">{t('userProfile.noCars')}</Typography>
          ) : (
            <Grid container spacing={3}>
              {cars.map((car) => (
                <Grid key={car.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card>
                    <CardActionArea component={RouterLink} to={carUrl(car)}>
                      <CarCardMedia car={car} height={180} />
                      <CardContent>
                        <Typography variant="h6">{car.display_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {car.year} {car.make} {car.model}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      </Stack>
    </Container>
  );
}
